// Общие элементы экрана «Настройки» и его окон: строки-переключатели, выбор из списка,
// заголовки разделов, шапка окна, строка статуса. Один вид для уведомлений, баннеров и iikoCloud.
import React from 'react';
import { Box, Button, IconButton, Switch, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CARD_LABEL_COLOR, DIVIDER_COLOR, MUTED_TEXT_COLOR } from '../../styles/tokens';

export const ACCENT = '#ff7043';
export const ACCENT_TEXT = '#1a0f0a';
export const ERROR_COLOR = '#ff6b6b';
export const SUCCESS_COLOR = '#66bb6a';
export const WARNING_COLOR = '#f0a43a';

// Небольшая кнопка в шапке карточки («Добавить», «Подключить»)
export const smallButtonSx = {
  height: 36,
  px: 1.5,
  borderRadius: '10px',
  fontSize: '0.875rem',
  textTransform: 'none',
  color: '#e4e4e7',
  backgroundColor: 'rgba(255,255,255,0.06)',
  boxShadow: 'none',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', boxShadow: 'none' },
};

export const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT, opacity: 1 },
};

// Строка с переключателем во всю ширину; первая — без разделителя сверху
export function ToggleRow({ id, label, hint, checked, disabled, onChange, first }) {
  return (
    <Box
      component="label"
      htmlFor={id}
      sx={{
        minHeight: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        cursor: disabled ? 'default' : 'pointer',
        borderTop: first ? 'none' : `1px solid ${DIVIDER_COLOR}`,
      }}
    >
      <Box sx={{ minWidth: 0, py: hint ? 0.75 : 0 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: '15px', color: '#f5f5f5', wordBreak: 'break-word' }}>
          {label}
        </Typography>
        {hint && (
          <Typography component="span" sx={{ display: 'block', fontSize: '13px', color: MUTED_TEXT_COLOR }}>
            {hint}
          </Typography>
        )}
      </Box>
      <Switch id={id} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} sx={switchSx} />
    </Box>
  );
}

export function SectionTitle({ children, sx }) {
  return (
    <Typography
      sx={{
        mt: 2,
        mb: 0.5,
        fontSize: '12px',
        fontWeight: 600,
        color: MUTED_TEXT_COLOR,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// Выбор одного варианта из списка — строки как в списке серверов (role="radio"); columns > 1 — сеткой
export function ChoiceList({ label, options, value, onChange, disabled, columns = 1 }) {
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: '2px' }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            key={option.value}
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled || option.disabled || undefined}
            tabIndex={disabled || option.disabled ? -1 : 0}
            onClick={() => !disabled && !option.disabled && onChange(option.value)}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === ' ') && !disabled && !option.disabled) {
                event.preventDefault();
                onChange(option.value);
              }
            }}
            sx={{
              minHeight: 48,
              boxSizing: 'border-box',
              px: 1.25,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderRadius: '10px',
              cursor: disabled || option.disabled ? 'default' : 'pointer',
              opacity: option.disabled ? 0.5 : 1,
              backgroundColor: selected ? 'rgba(255,112,67,0.08)' : 'transparent',
              outline: 'none',
              '&:hover': { backgroundColor: selected ? 'rgba(255,112,67,0.08)' : 'rgba(255,255,255,0.04)' },
              '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${ACCENT}` },
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                flexShrink: 0,
                width: 18,
                height: 18,
                borderRadius: '50%',
                boxSizing: 'border-box',
                border: selected ? `5px solid ${ACCENT}` : '2px solid rgba(255,255,255,0.3)',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '15px', color: '#f5f5f5', wordBreak: 'break-word' }}>{option.label}</Typography>
              {option.hint && <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>{option.hint}</Typography>}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// Шапка окна ResponsiveDialog: заголовок (id для aria-labelledby), подзаголовок, крестик
export function DialogHeader({ id, title, subtitle, onClose, disabled }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -1, mr: -1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography id={id} component="h2" sx={{ fontSize: '18px', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography noWrap title={subtitle} sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <IconButton
        onClick={onClose}
        disabled={disabled}
        aria-label="Закрыть"
        sx={{ width: 44, height: 44, flexShrink: 0, color: CARD_LABEL_COLOR }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
}

// Результат действия строкой (ошибка — role="alert")
export function StatusText({ status, sx }) {
  if (!status) return null;
  const color = { error: ERROR_COLOR, success: SUCCESS_COLOR, warning: WARNING_COLOR }[status.type] || CARD_LABEL_COLOR;
  return (
    <Typography
      role={status.type === 'error' ? 'alert' : 'status'}
      sx={{ fontSize: '14px', color, whiteSpace: 'pre-line', wordBreak: 'break-word', ...sx }}
    >
      {status.text}
    </Typography>
  );
}

// Маленькая плашка-статус: цветная точка и текст
export function Pill({ color = MUTED_TEXT_COLOR, children }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: 24,
        px: 1,
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        color,
        backgroundColor: 'rgba(255,255,255,0.06)',
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color }} />
      {children}
    </Box>
  );
}

// Кнопка-строка в окне действий: во всю ширину, слева иконка
export function ActionRow({ icon: Icon, label, hint, onClick, disabled, danger, first }) {
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: '100%',
        minHeight: 48,
        justifyContent: 'flex-start',
        gap: 1.5,
        px: 1,
        borderRadius: '8px',
        textTransform: 'none',
        textAlign: 'left',
        color: danger ? ERROR_COLOR : '#f5f5f5',
        borderTop: first ? 'none' : `1px solid ${DIVIDER_COLOR}`,
        '&:hover': { backgroundColor: danger ? 'rgba(255,107,107,0.08)' : 'rgba(255,255,255,0.04)' },
        '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
      }}
    >
      {Icon && <Icon sx={{ fontSize: 20, color: danger ? ERROR_COLOR : CARD_LABEL_COLOR, flexShrink: 0 }} />}
      <Box component="span" sx={{ minWidth: 0 }}>
        <Box component="span" sx={{ display: 'block', fontSize: '15px' }}>{label}</Box>
        {hint && <Box component="span" sx={{ display: 'block', fontSize: '13px', color: MUTED_TEXT_COLOR }}>{hint}</Box>}
      </Box>
    </Button>
  );
}
